import { z } from 'zod'
import { APIClient } from './api-client'
import { ConfigManager } from './config'
import { EmbeddedModal } from './embedded-modal'
import { InitOptions, InitOptionsSchema, ModalOptions, ModalOptionsSchema } from './schemas'

class NiftWebSDK {
  private config: ConfigManager
  private embeddedModal: EmbeddedModal | null = null
  private apiClient: APIClient
  private isEligible: boolean = false

  constructor() {
    this.config = ConfigManager.getInstance()
    this.apiClient = new APIClient()
  }

  async init(options: InitOptions): Promise<void> {
    try {
      const validatedOptions = InitOptionsSchema.parse(options)
      this.config.setConfig(validatedOptions)

      this.config.setSkipOffer(validatedOptions.skipOffer ?? false)
      this.config.setShowClose(validatedOptions.showClose ?? false)

      await this.config.resolveApiUrl()

      const code = this.config.getCode()
      const customer = this.config.getCustomer()

      try {
        const activationResult = await this.apiClient.checkActivation(code, customer)

        if (activationResult.status === 'available') {
          this.isEligible = true
          if (activationResult.theme) {
            this.config.setTheme(activationResult.theme)
          }
          if (activationResult.activationLink) {
            this.config.setActivationLink(activationResult.activationLink)
            this.config.updateCustomerFromActivationLink(activationResult.activationLink)
          }
          if (activationResult.partnerName) {
            this.config.setPartnerName(activationResult.partnerName)
          }
          if (activationResult.referralCode) {
            this.config.setReferralCode(activationResult.referralCode)
          }
        } else {
          this.isEligible = false
        }
      } catch (error) {
        console.warn('Failed to check activation status:', error)
        this.isEligible = true
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const messages = error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(', ')
        throw new Error(`Invalid initialization options: ${messages}`)
      }
      throw error
    }
  }

  showEmbeddedModal(options?: ModalOptions): void {
    try {
      if (!this.config.isInitialized()) {
        throw new Error('SDK not initialized. Call NiftWebSDK.init() first.')
      }

      const validatedOptions = options ? ModalOptionsSchema.parse(options) : {}

      if (!this.isEligible) {
        validatedOptions.onIneligible?.()
        return
      }

      if (this.embeddedModal?.isOpen()) {
        this.embeddedModal.close()
      }

      const customer = this.config.getCustomer()
      const code = this.config.getCode()
      const clientId = this.config.getClientId()
      const partnerName = this.config.getStoredPartnerName() || this.config.getPartnerName()
      const theme = this.config.getTheme()
      const apiUrl = this.config.getApiUrl()
      const referralCode = this.config.getReferralCode()

      this.apiClient.sendEvent('open_modal').catch(console.warn)

      validatedOptions.onShow?.()

      this.embeddedModal = new EmbeddedModal({
        customer: customer,
        partnerName: partnerName,
        theme: theme || undefined,
        clientId: clientId,
        code: code,
        apiUrl: apiUrl,
        referralCode: referralCode || undefined,
        skipOffer: this.config.getSkipOffer(),
        showClose: this.config.getShowClose(),
      })

      const handleClose = () => {
        this.apiClient.sendEvent('close_modal').catch(console.warn)
        validatedOptions.onClose?.()
      }

      const handleSelection = () => {
        validatedOptions.onSelection?.()
      }

      this.embeddedModal.show(handleClose, handleSelection)
    } catch (error) {
      if (error instanceof z.ZodError) {
        const messages = error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(', ')
        throw new Error(`Invalid modal options: ${messages}`)
      }
      throw error
    }
  }

  close(): void {
    this.embeddedModal?.close()
  }

  isInitialized(): boolean {
    return this.config.isInitialized()
  }

  reset(): void {
    this.close()
    this.config.reset()
  }
}

const sdk = new NiftWebSDK()

export default sdk
export { sdk as NiftWebSDK }

if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window as any).NiftWebSDK = sdk
}

export type { Customer, GiftCertificate, InitOptions, ModalOptions } from './schemas'

export type { AppEvent } from './event-tracker'
